import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, setAuth, clearAuth } from "@/lib/api";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { UserPlus, LogIn, Loader2, Gift } from "lucide-react";

export default function RiderAuth({ mode = "login" }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = (params.get("ref") || "").trim().toUpperCase();
  const [f, setF] = useState({ email: "", password: "", name: "", mobile: "" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const isSignup = mode === "signup";

  useEffect(() => { clearAuth(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (isSignup && !acceptTerms) {
      toast.error("Please accept the Terms & Conditions to create your account.");
      return;
    }
    setLoading(true);
    try {
      const path = isSignup ? "/auth/register" : "/auth/login";
      const body = isSignup
        ? { ...f, accept_terms: acceptTerms, referral_code: refFromUrl || undefined }
        : { email: f.email, password: f.password };
      const { data } = await api.post(path, body);
      if (data?.mfa_required) { toast.error("This account has admin MFA — use /admin/login"); return; }
      setAuth(data.token, data.user);
      toast.success(isSignup ? `Welcome, ${data.user.name}` : `Kia ora, ${data.user.name}`);
      if (data.user.role === "corporate_admin") nav("/corporate");
      else nav("/my-rides");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) toast.error(detail || "Too many attempts — try again shortly", { duration: 8000 });
      else if (status === 401) toast.error("Wrong email or password");
      else if (status === 400) toast.error(detail || "Please check the form");
      else if (!e?.response) toast.error("Network / proxy error — check your connection");
      else toast.error(detail || `Failed (${status})`);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#090D14] text-white">
      <Header />
      <div className="max-w-md mx-auto px-4 pt-32 pb-20">
        <div className="glass-card rounded-2xl p-8 gold-border fade-up">
          <div className="text-caption mb-2 flex items-center gap-1.5">
            {isSignup ? <UserPlus className="w-3.5 h-3.5" /> : <LogIn className="w-3.5 h-3.5" />}
            {isSignup ? "Create account" : "Sign in"}
          </div>
          <h1 className="font-display text-3xl font-bold">{isSignup ? "Join Apex Taxi" : "Welcome back"}</h1>
          <p className="text-sm text-slate-400 mt-1">{isSignup ? "Save your details for one-tap bookings." : "Sign in to see your ride history."}</p>
          {isSignup && refFromUrl && (
            <div className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-3 py-2.5 flex items-center gap-2" data-testid="signup-referral-banner">
              <Gift className="w-4 h-4 text-emerald-300 shrink-0" />
              <div className="text-xs text-emerald-200 leading-relaxed">
                Referred by <b className="font-mono text-emerald-300">{refFromUrl}</b> — you'll get <b>NZ$5 off</b> your first ride once it's complete.
              </div>
            </div>
          )}
          <form onSubmit={submit} className="mt-6 space-y-4">
            {isSignup && (
              <>
                <div><Label className="text-xs uppercase text-slate-400">Name</Label>
                  <Input data-testid="rider-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-black/30 border-white/10 h-11 mt-2" /></div>
                <div><Label className="text-xs uppercase text-slate-400">Mobile</Label>
                  <Input data-testid="rider-mobile" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} placeholder="+64 …" className="bg-black/30 border-white/10 h-11 mt-2" /></div>
              </>
            )}
            <div><Label className="text-xs uppercase text-slate-400">Email</Label>
              <Input data-testid="rider-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-black/30 border-white/10 h-11 mt-2" /></div>
            <div><Label className="text-xs uppercase text-slate-400">Password</Label>
              <Input data-testid="rider-password" required type="password" minLength={isSignup ? 8 : 1} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-black/30 border-white/10 h-11 mt-2" /></div>
            {isSignup && (
              <label className="flex items-start gap-2.5 pt-1 cursor-pointer group" data-testid="rider-terms-wrap">
                <Checkbox
                  checked={acceptTerms}
                  onCheckedChange={(v) => setAcceptTerms(!!v)}
                  className="mt-0.5 border-red-400/40 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                  data-testid="rider-accept-terms"
                />
                <span className="text-xs text-slate-400 leading-relaxed group-hover:text-slate-300">
                  I accept the{" "}
                  <Link to="/terms" target="_blank" className="text-red-300 hover:text-red-200 underline underline-offset-2" data-testid="rider-terms-link">Terms & Conditions</Link>
                  {" "}and{" "}
                  <Link to="/privacy" target="_blank" className="text-red-300 hover:text-red-200 underline underline-offset-2" data-testid="rider-privacy-link">Privacy Policy</Link>.
                </span>
              </label>
            )}
            <Button data-testid="rider-submit" type="submit" disabled={loading || (isSignup && !acceptTerms)} className="w-full btn-gold rounded-lg h-11 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {isSignup ? "Create account" : "Sign in"}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-400">
            {isSignup ? <>Already have an account? <Link to="/login" className="text-amber-400 hover:text-amber-300">Sign in</Link></>
                     : <>New to Apex? <Link to="/signup" className="text-amber-400 hover:text-amber-300">Create account</Link></>}
          </div>
        </div>
      </div>
    </div>
  );
}
